import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  LayoutDashboard, Users, FileText, Wrench,
  ArrowLeftRight, Receipt, BarChart3, Settings, CalendarClock,
  Cpu, Hammer, LogOut, ChevronDown, ShoppingCart, Truck, Building2, Check, Wallet, Search,
} from 'lucide-react'
import { useNav, type Page } from '../../stores/navigationStore.js'
import { useAuthStore } from '../../stores/authStore.js'
import { useAlertsStore } from '../../stores/alertsStore.js'
import { cn, humanizeError } from '../../lib/utils.js'
import { trpc } from '../../api/index.js'
import { useComutaPunctLucru } from '../../lib/comutaPunctLucru.js'
import { useToast } from '../common/Toast'

interface NavItem {
  icon: React.ElementType
  label: string
  page: Page
  disabled?: boolean
  shortcut?: string
}

const groups: { title: string; items: NavItem[] }[] = [
  { title: '', items: [{ icon: LayoutDashboard, label: 'Dashboard', page: 'dashboard', shortcut: '⌃1' }] },
  { title: 'Clienți', items: [{ icon: Users, label: 'Clienți', page: 'clienti', shortcut: '⌃2' }] },
  {
    title: 'Operațional',
    items: [
      { icon: FileText,       label: 'Contracte',       page: 'contracte',     shortcut: '⌃4' },
      { icon: Wrench,         label: 'Inventar',        page: 'echipamente',   shortcut: '⌃3' },
      { icon: CalendarClock,  label: 'Rezervări',       page: 'rezervari',     shortcut: '⌃6' },
      { icon: ArrowLeftRight, label: 'Predare / Retur', page: 'predare-retur', shortcut: '⌃7' },
      { icon: Truck,          label: 'Transfer',        page: 'transfer' },
      { icon: Hammer,         label: 'Mentenanță',      page: 'mentenanta',    shortcut: '⌃8' },
    ],
  },
  {
    title: 'Fiscal',
    items: [
      { icon: Receipt,      label: 'Facturare',  page: 'facturare',  shortcut: '⌃5' },
      { icon: Wallet,       label: 'Încasări',   page: 'incasari' },
      { icon: ShoppingCart, label: 'Cheltuieli', page: 'cheltuieli' },
    ],
  },
  { title: 'Hardware', items: [{ icon: Cpu,      label: 'Hardware & AMEF', page: 'hardware' }] },
  { title: 'Rapoarte', items: [{ icon: BarChart3, label: 'Rapoarte',        page: 'rapoarte', shortcut: '⌃9' }] },
  { title: 'Sistem',   items: [{ icon: Settings,  label: 'Configurări',     page: 'setari' }] },
]

const ROL_COLORS: Record<string, string> = {
  admin: 'text-red-400', operator: 'text-brand-400', casier: 'text-green-400', vizualizare: 'text-slate-400',
}
const ROL_BG: Record<string, string> = {
  admin:       'bg-red-900/30 border-red-800/40 text-red-200',
  operator:    'bg-brand-900/30 border-brand-800/40 text-brand-200',
  casier:      'bg-green-900/30 border-green-800/40 text-green-200',
  vizualizare: 'bg-slate-800 border-slate-700 text-slate-400',
}

// ─── Logo + Brand (centrat) ───────────────────────────────────────────────────

function SidebarHeader() {
  const q = trpc.setari.getAspect.useQuery(undefined, { staleTime: Infinity, retry: false })
  const [logo, setLogo] = useState<string | null>(() => {
    try { return localStorage.getItem('edrikom_logo') } catch { return null }
  })
  useEffect(() => {
    if (q.data?.logoBase64 !== undefined) setLogo(q.data.logoBase64 ?? null)
  }, [q.data?.logoBase64])

  return (
    <div className="flex items-center gap-4 px-5 pt-6 pb-5">
      {/* Logo firmă — mare, fără padding intern */}
      <div className="w-[62px] h-[62px] rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden shadow-md">
        {logo
          ? <img src={logo} alt="Logo" className="w-full h-full object-contain" />
          : <img src="/favicon.svg" alt="rentCORE" className="w-10 h-10" />
        }
      </div>

      {/* Brand text */}
      <div>
        <p className="text-[20px] font-extrabold text-white leading-none tracking-tight">
          rent<span className="text-brand-400">CORE</span>
        </p>
        <p className="text-[11px] text-slate-500 leading-tight mt-1.5">Gestiune închirieri</p>
      </div>
    </div>
  )
}

// ─── Punct de lucru ───────────────────────────────────────────────────────────

function StoreSwitcher() {
  const { session } = useAuthStore()
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const puncteQ   = trpc.auth.getPuncteLucruAccesibile.useQuery(undefined, { enabled: !!session })
  const switchMut = useComutaPunctLucru({
    onSuccess: () => setOpen(false),
    onError: (e) => {
      // Fără `onError`, o comutare eșuată (punct dezactivat, acces revocat) nu producea
      // niciun feedback: meniul rămânea deschis și părea că nu s-a întâmplat nimic.
      toast.error(humanizeError((e as { message?: string } | null)?.message))
      setOpen(false)
    },
  })

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const puncte  = puncteQ.data ?? []
  const current = puncte.find(p => p.id === session?.punctLucruId) ?? puncte[0]
  const multi   = puncte.length > 1

  // Fallback la datele din sesiune dacă query-ul încă se încarcă
  const displayNume  = current?.denumire ?? session?.punctDenumire ?? '—'
  const displayCod   = (current as any)?.codul ?? session?.punctCodul ?? ''

  return (
    <div ref={ref} className="relative mx-3 mb-3">
      <button
        onClick={() => multi && setOpen(o => !o)}
        aria-haspopup={multi ? 'listbox' : undefined}
        aria-expanded={multi ? open : undefined}
        aria-label={`Punct de lucru: ${displayNume}${multi ? ' — schimbă punctul de lucru' : ''}`}
        className={cn(
          'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left',
          multi
            ? 'bg-slate-900/60 border-slate-800 hover:bg-slate-800/80 hover:border-slate-700'
            : 'bg-slate-900/40 border-slate-800/60 cursor-default',
        )}
      >
        <div className="w-6 h-6 rounded-lg bg-brand-600/20 border border-brand-500/30 flex items-center justify-center shrink-0">
          <Building2 size={12} className="text-brand-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-200 truncate leading-tight">{displayNume}</p>
          {displayCod && (
            <p className="text-[9px] font-mono text-slate-600 leading-none mt-0.5">{displayCod}</p>
          )}
        </div>
        {multi && <ChevronDown size={11} className={cn('text-slate-600 shrink-0 transition-transform duration-200', open && 'rotate-180')} />}
      </button>

      {open && (
        <div role="listbox" aria-label="Puncte de lucru" className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700/80 rounded-xl overflow-hidden shadow-2xl z-50">
          <p className="px-3 py-2 text-[9px] font-semibold text-slate-500 uppercase tracking-widest border-b border-slate-700/60">Punct de lucru</p>
          {puncte.map(p => {
            const isCurrent = p.id === session?.punctLucruId
            const isLoading = switchMut.isPending && switchMut.variables === p.id
            return (
              <button key={p.id} role="option" aria-selected={isCurrent} onClick={() => !isCurrent && switchMut.mutate(p.id)} disabled={isCurrent || switchMut.isPending}
                className={cn('w-full flex items-center gap-2.5 px-3 py-2.5 text-xs transition-colors text-left',
                  isCurrent ? 'text-brand-300 bg-brand-600/10 cursor-default' : 'text-slate-300 hover:bg-slate-700/80 hover:text-white')}>
                <Building2 size={11} className="shrink-0 opacity-60" />
                <div className="flex-1 min-w-0">
                  {(p as any).codul && <span className="font-mono text-[9px] text-slate-500 mr-1">{(p as any).codul}</span>}
                  <span className="truncate">{p.denumire}</span>
                </div>
                {isCurrent && <Check size={11} className="shrink-0 text-brand-400" />}
                {isLoading && <div className="w-3 h-3 border border-slate-500 border-t-white rounded-full animate-spin shrink-0" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── User card (jos) ──────────────────────────────────────────────────────────

function UserCard() {
  const { session, logout } = useAuthStore()
  if (!session) return null

  const initials = session.numeComplet
    ?.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() ?? '?'

  return (
    <div className="mx-3 my-3 flex items-center gap-3 px-3 py-3 rounded-xl bg-slate-900/80 border border-slate-800/80">
      <div className={cn('w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-[12px] font-bold border', ROL_BG[session.rol] ?? ROL_BG.vizualizare)}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        {/* Permite wrap pe 2 rânduri dacă nu încape */}
        <p className="text-[12px] font-semibold text-slate-100 leading-snug break-words">
          {session.numeComplet}
        </p>
        <p className={cn('text-[10px] capitalize leading-tight mt-0.5', ROL_COLORS[session.rol] ?? 'text-slate-500')}>
          {session.rol}
        </p>
      </div>
      <button onClick={logout} title="Deconectare" aria-label="Deconectare"
        className="p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-900/20 transition-colors shrink-0">
        <LogOut size={14} />
      </button>
    </div>
  )
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

/** `true` când layout-ul e cel de desktop (sidebar static, mereu vizibil). */
function useEsteDesktop(): boolean {
  const query = '(min-width: 1024px)'
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const h = () => setDesktop(mq.matches)
    h()
    mq.addEventListener('change', h)
    return () => mq.removeEventListener('change', h)
  }, [])
  return desktop
}

export function Sidebar({ isOpen, onClose, onSearchOpen }: { isOpen?: boolean; onClose?: () => void; onSearchOpen?: () => void }) {
  const { page, navigate } = useNav()
  const { alerte, setAlerte } = useAlertsStore()
  const { session } = useAuthStore()
  const esteDesktop = useEsteDesktop()
  // Pe mobil, drawer-ul închis e doar translatat în afara ecranului — fără `inert`, Tab-ul
  // și cititoarele de ecran ajungeau în continuare în linkurile lui invizibile.
  const ascunsPeMobil = !esteDesktop && !isOpen

  const alerteQuery = trpc.dashboard.alerte.useQuery(undefined, {
    refetchInterval: 5 * 60 * 1000,
    enabled: !!session,
  })
  useEffect(() => {
    if (alerteQuery.data) setAlerte(alerteQuery.data as any)
  }, [alerteQuery.data, setAlerte])

  const handleNavigate = useCallback((p: Page) => {
    navigate(p)
    onClose?.()  // închide drawer-ul pe mobil după navigare
  }, [navigate, onClose])

  return (
    <aside
      inert={ascunsPeMobil}
      aria-label="Meniu principal"
      className={cn(
      // Layout de bază
      'w-60 bg-slate-950 border-r border-slate-800/60 flex flex-col shrink-0 overflow-hidden',
      // Pe mobil: drawer fix, în afara flow-ului
      'fixed inset-y-0 left-0 z-50 shadow-2xl',
      'transition-transform duration-300 ease-in-out',
      isOpen ? 'translate-x-0' : '-translate-x-full',
      // Pe desktop: static în flow, mereu vizibil
      'lg:relative lg:z-auto lg:translate-x-0 lg:shadow-none lg:h-full',
    )}>

      {/* ── HEADER FIX: Logo + Punct de lucru ── */}
      <div className="shrink-0">
        <SidebarHeader />

        <div className="mx-3 mb-3 h-px bg-slate-800/70" />

        <StoreSwitcher />

        {onSearchOpen && (
          <div className="px-3 mb-3">
            <button
              onClick={() => { onClose?.(); onSearchOpen() }}
              aria-label="Căutare globală"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/40 border border-slate-800/60 text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors text-left"
            >
              <Search size={13} className="shrink-0" />
              <span className="flex-1 text-[12px]">Caută…</span>
              <kbd className="hidden lg:inline text-[9px] font-mono text-slate-600">Ctrl K</kbd>
            </button>
          </div>
        )}

        <div className="mx-3 mb-1 h-px bg-slate-800/70" />
      </div>

      {/* ── NAV: derulează dacă nu încape ── */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
        {groups.map((group, gi) => (
          <div key={gi} className={gi > 0 ? 'mt-0.5' : ''}>
            {group.title && (
              <div className="flex items-center gap-2 px-1 mb-0.5 mt-4">
                <span className="text-[9px] font-semibold text-slate-600 tracking-widest uppercase whitespace-nowrap">
                  {group.title}
                </span>
                <div className="flex-1 h-px bg-slate-800/80" />
              </div>
            )}

            {group.items.map(item => {
              const Icon   = item.icon
              const active = page === item.page
              let alertCount = 0
              if (alerte) {
                // Aceleași alerte CRITICE (P0) pe contracte ca în dashboard, fără dublă numărare:
                // contractele cu penalități sunt (aproape) toate și „cu termenul depășit", deci
                // suma lor număra același contract de două ori. Luăm maximul celor două mulțimi
                // suprapuse + cele returnate nefacturate (disjuncte: alt status).
                if (item.page === 'contracte') alertCount =
                  Math.max(alerte.contracteExpirate ?? 0, alerte.contracteCuPenalitati ?? 0) + (alerte.returnateFaraFactura ?? 0)
                if (item.page === 'facturare') alertCount = alerte.facturiScadente ?? 0
                if (item.page === 'clienti')   alertCount = alerte.clientiCiExpirate ?? 0
              }

              return (
                <div key={item.page} className="relative">
                  {active && (
                    <span className="absolute left-0 top-[5px] bottom-[5px] w-[3px] rounded-r-full bg-brand-400" />
                  )}
                  <button
                    disabled={item.disabled}
                    onClick={() => !item.disabled && handleNavigate(item.page)}
                    className={cn(
                      'w-full flex items-center gap-3 pl-4 pr-3 py-[6px] rounded-xl text-[13px] font-medium transition-all duration-150 text-left group/item',
                      active
                        ? 'bg-brand-600/10 text-brand-300'
                        : item.disabled
                          ? 'text-slate-700 cursor-not-allowed'
                          : 'text-slate-500 hover:bg-slate-800/60 hover:text-slate-100',
                    )}
                  >
                    <Icon size={14} className={cn('shrink-0 transition-colors', active ? 'text-brand-400' : 'text-slate-600 group-hover/item:text-slate-300')} />
                    <span className="flex-1 leading-none">{item.label}</span>
                    {alertCount > 0 && (
                      <span aria-label={`${alertCount} alerte critice`} className="flex items-center justify-center min-w-[17px] h-[17px] px-1 bg-red-500/90 text-white text-[9px] font-bold rounded-full leading-none">
                        {alertCount > 99 ? '99+' : alertCount}
                      </span>
                    )}
                    {item.shortcut && !active && alertCount === 0 && (
                      <span className="text-[9px] text-slate-700 font-mono group-hover/item:text-slate-600 transition-colors">
                        {item.shortcut}
                      </span>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        ))}
      </nav>

      {/* ── USER FIX: jos de tot ── */}
      <div className="shrink-0 border-t border-slate-800/60">
        <UserCard />
      </div>
    </aside>
  )
}
